import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@loot-os/ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown, XIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { useTRPC } from "../lib/trpc";

/**
 * Picks tenants by id from every tenant on the platform. An empty pick means shared, which is
 * what the chips say when none is chosen. An id the list no longer has (a deleted tenant) still
 * shows as a chip, by id, so staff can see it and take it off.
 */
export function TenantsPicker({
  id,
  value,
  disabled,
  onChange,
}: {
  /** Ties the trigger to the field's `<Label htmlFor>`. */
  id: string;
  value: string[];
  disabled?: boolean;
  onChange: (tenantIds: string[]) => void;
}) {
  const trpc = useTRPC();
  // The dialog remounts on every opening, and the tenant list changes rarely.
  const tenants = useQuery({ ...trpc.platform.tenants.list.queryOptions(), staleTime: 60_000 });
  const [open, setOpen] = useState(false);
  const byName = useMemo(
    () => [...(tenants.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [tenants.data],
  );
  const nameById = useMemo(() => new Map(byName.map((t) => [t.id, t.name] as const)), [byName]);
  const options = byName.filter((tenant) => !value.includes(tenant.id));

  return (
    <div className="flex flex-col gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={
            <Button
              id={id}
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || tenants.isLoading}
              className="h-8 w-full justify-between font-normal"
            />
          }
        >
          <span className="truncate text-muted-foreground">Add a tenant…</span>
          <ChevronsUpDown className="size-3.5 opacity-60" />
        </PopoverTrigger>
        <PopoverContent className="w-[22rem] p-0" align="start">
          <Command>
            <CommandInput placeholder="Tenant name, slug or id" />
            <CommandList>
              <CommandEmpty>No other tenant.</CommandEmpty>
              {options.map((tenant) => (
                <CommandItem
                  key={tenant.id}
                  value={`${tenant.name} ${tenant.id}`}
                  keywords={[tenant.slug]}
                  onSelect={() => {
                    onChange([...value, tenant.id]);
                    setOpen(false);
                  }}
                >
                  {tenant.name} <span className="font-mono text-muted-foreground">{tenant.id}</span>
                </CommandItem>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className="flex flex-wrap gap-1.5">
        {value.length === 0 ? (
          <Badge variant="outline">Shared with every tenant</Badge>
        ) : (
          value.map((tenantId) => (
            <Badge key={tenantId} variant="secondary" className="pr-1">
              {nameById.get(tenantId) ?? <span className="font-mono">{tenantId}</span>}
              <button
                type="button"
                aria-label={`Remove ${nameById.get(tenantId) ?? tenantId}`}
                disabled={disabled}
                onClick={() => onChange(value.filter((other) => other !== tenantId))}
                className="rounded-full opacity-60 hover:opacity-100"
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}
